export const MONTH_TREND_MONTH_PREFIXES = [
    'jan',
    'feb',
    'mar',
    'apr',
    'may',
    'jun',
    'jul',
    'aug',
    'sep',
    'oct',
    'nov',
    'dec',
];

/**
 * Column ids for monthly-trend / forecast-vs-actuals views (`jan{year}Forecast`, …, `ytd{year}Forecast`).
 * Must stay aligned with `GET /api/v1/monthly-trend-table-data` row keys for `forecastYear`.
 */
export function buildMonthlyTrendColumnIds() {
    const base = [
        'supplySku',
        'productName',
        'category',
        'subCategory',
        'status',
        'forecastable',
        'productLine',
        'launchDate',
        'inactiveDate',
        'supplier',
        'warehouse',
        'msrp',
    ];
    const trend = [];
    for (const m of MONTH_TREND_MONTH_PREFIXES) {
        trend.push(`${m}Forecast`, `${m}Actual`, `${m}Variance`);
    }
    trend.push(
        `ytdForecast`,
        `ytdActual`,
        `ytdVariance`,
        'variancePercent',
        'varianceFlag'
    );
    return [...base, ...trend];
}

/** Trend column ids (Forecast / Actual / Variance) for calendar-closed months in the planning year. */
export function buildTrendColumnIdsThroughClosedMonths(closedMonthCount) {
    const ids = [];
    const limit = Math.min(Math.max(Number(closedMonthCount) || 0, 0), MONTH_TREND_MONTH_PREFIXES.length);
    for (let i = 0; i < limit; i += 1) {
        const month = MONTH_TREND_MONTH_PREFIXES[i];
        ids.push(`${month}Forecast`, `${month}Actual`, `${month}Variance`);
    }
    return ids;
}

const TREND_VIEWS_WITH_CLOSED_MONTHS = new Set(["monthly-trend", "forecast-vs-actuals"]);

/** Calendar-closed months in the planning year (Jun → 5 = Jan–May). Past years → 12. */
export function countClosedPlanningYearMonths(planningYear) {
    const fy = Number.parseInt(String(planningYear ?? ""), 10);
    if (!Number.isFinite(fy)) return 0;
    const now = new Date();
    const cy = now.getFullYear();
    const cm = now.getMonth();
    if (fy < cy) return 12;
    if (fy > cy) return 0;
    return cm;
}

function trendMonthIndexFromColumnId(columnId) {
    if (!columnId || typeof columnId !== "string") return -1;
    for (let i = 0; i < MONTH_TREND_MONTH_PREFIXES.length; i += 1) {
        const month = MONTH_TREND_MONTH_PREFIXES[i];
        if (!columnId.startsWith(month)) continue;
        const suffix = columnId.slice(month.length);
        if (suffix === "Forecast" || suffix === "Actual" || suffix === "Variance") {
            return i;
        }
    }
    return -1;
}

/** Drops trend month columns beyond the last calendar-closed month. */
export function filterTrendColumnsThroughClosedMonths(columnIds, closedMonthCount) {
    const limit = Math.min(Math.max(Number(closedMonthCount) || 0, 0), 12);
    return (columnIds || []).filter((id) => {
        const monthIdx = trendMonthIndexFromColumnId(id);
        if (monthIdx === -1) return true;
        return monthIdx < limit;
    });
}

function appendClosedMonthTrendDefaults(viewId, template, planningYear) {
    if (!TREND_VIEWS_WITH_CLOSED_MONTHS.has(viewId) || planningYear == null || planningYear === "") {
        return template;
    }
    const staticTemplate = (template || []).filter((id) => trendMonthIndexFromColumnId(id) === -1);
    const closed = countClosedPlanningYearMonths(planningYear);
    return [...staticTemplate, ...buildTrendColumnIdsThroughClosedMonths(closed)];
}

/**
 * Column ids for forecast-vs-actuals (annual units + monthly trend keys + inventory columns).
 * Must align with `GET /api/v1/forecast-vs-actuals-table-data` for `forecastYear`.
 */
export function buildForecastVsActualsColumnIds() {
    const base = [
        'supplySku',
        'productName',
        'category',
        'subCategory',
        'annualUnits',
        'status',
        'forecastable',
        'productLine',
        'launchDate',
        'inactiveDate',
        'supplier',
        'warehouse',
        'msrp',
    ];
    const trend = [];
    for (const m of MONTH_TREND_MONTH_PREFIXES) {
        trend.push(`${m}Forecast`, `${m}Actual`, `${m}Variance`);
    }
    trend.push(
        `ytdForecast`,
        `ytdActual`,
        `ytdVariance`,
        'variancePercent',
        'varianceFlag',
        'totalInventory',
        'targetCoverageDays',
        'overstockThreshold',
        'overstockFlag',
        'overstockLevel',
        'excessUnits',
        'excessValue',
        'action',
        'governance',
    );
    return [...base, ...trend];
}

export const PRESET_COLUMN_IDS = {
    "sku-allocation": [
      "supplySku",
      "productName",
      "category",
      "subCategory",
      "status",
      "forecastable",
      "productLine",
      "launchDate",
      "inactiveDate",
      "supplier",
      "warehouse",
      "msrp",
      "planned_gross_price",
      "ltmRevenue",
      "ltmPercent",
      "overridePercent",
      "newProductOverride",
      "finalPercent",
      "eligibleFinalPercent",
      "subcategoryEligibleTotalPercent",
      "adjustedFinalPercent",
      "allocationTotalCheck",
      "forecastableCheck",
      "overrideConflictCheck",
      "forecastInputCheck",
      "totalUnitsCheck",
      "skuForecastRevenue",
      "subcategoryForecastRevenue",
      "annualUnits",
      "janRevenue",
      "febRevenue",
      "marRevenue",
      "aprRevenue",
      "mayRevenue",
      "junRevenue",
      "julRevenue",
      "augRevenue",
      "sepRevenue",
      "octRevenue",
      "novRevenue",
      "decRevenue",
      "ytdTotals",
      "recalcTotal2025",
    ],
    "forecast-demand": [],
    "eligibility-validation": [
      "supplySku",
      "productName",
      "category",
      "subCategory",
      "status",
      "forecastable",
      "productLine",
      "launchDate",
      "inactiveDate",
      "supplier",
      "warehouse",
      "msrp",
      "ltmRevenue",
      "ltmPercent",
      "overridePercent",
      "newProductOverride",
      "finalPercent",
      "eligibleFinalPercent",
      "subcategoryEligibleTotalPercent",
      "adjustedFinalPercent",
      "allocationTotalCheck",
      "forecastableCheck",
      "overrideConflictCheck",
      "forecastInputCheck",
      "skuForecastRevenue",
      "subcategoryForecastRevenue",
      "annualUnits",
    ],
    "monthly-trend": buildMonthlyTrendColumnIds(),
    historical: [
      "supplySku",
      "productName",
      "category",
      "subCategory",
      "status",
      "forecastable",
      "productLine",
      "launchDate",
      "inactiveDate",
      "supplier",
      "warehouse",
      "msrp",
      "janRevenue",
      "febRevenue",
      "marRevenue",
      "aprRevenue",
      "mayRevenue",
      "junRevenue",
      "julRevenue",
      "augRevenue",
      "sepRevenue",
      "octRevenue",
      "novRevenue",
      "decRevenue",
      "ytdTotals",
      "recalcTotal2025",
    ],
    "forecast-vs-actuals": buildForecastVsActualsColumnIds(),
    "forecast-by-sku": [
      "supplySku",
      "productName",	
      "category",
      "subCategory",
      "status",
      "forecastable",
      "productLine",
      "launchDate",
      "inactiveDate", 
      "supplier",
      "warehouse",
      "msrp",
      "ltmRevenue",
      "ltmPercent",
      "overridePercent",
      "newProductOverride",
      "finalPercent",
      "eligibleFinalPercent",
      "subcategoryEligibleTotalPercent",
      "adjustedFinalPercent",
      "allocationTotalCheck",
      "forecastableCheck",
      "overrideConflictCheck",
      "forecastInputCheck",
      "totalUnitsCheck",
      "skuForecastRevenue",
      "subcategoryForecastRevenue",
      "annualUnits",
      "janUnits",
      "febUnits",
      "marUnits",
      "aprUnits",
      "mayUnits",
      "junUnits",
      "julUnits",
      "augUnits",
      "sepUnits",
      "octUnits",
      "novUnits",
      "decUnits",
      "orderEligible",
      "leadTimeDays",
      "coverageMonthsTarget",
      "coverageDaysTarget",
      "targetInventoryUnits",
      "annualUnits",
      "unitsSoldYtd",
      "lastClosedSalesMonth",
      "remainingAnnualDemand",
      "forecastUnitsNext3Months",
      "dailyDemand",
      "safetyStockDays",
      "leadTimeDemand",
      "safetyStockUnits",
      "reorderPointUnits",
      "daysUntilStockout",
      "stockoutDate",
      "nextPoRequiredDate",
      "replenishmentCashRequired",
      "onHandUnits",
      "onOrderUnits",
      "replacementOnHand",
      "replacementOnOrder",
      "projectedCoverageDays",
      "otbUnits",
      "annualOtbUnits",
      "totalBomCost",
      "estAnnualInventoryCost",
      "effectiveOnHand",
      "effectiveOnOrder",
      "unitsToOrder",
      "riskFlag",
      "approved",
      "approvedBy",
      "notes",
    ],
    "open-to-buy": [
      "supplySku",
      "productName",
      "category",
      "subCategory",
      "status",
      "forecastable",
      "productLine",
      "launchDate",
      "inactiveDate",
      "supplier",
      "warehouse",
      "msrp",
      "ltmRevenue",
      "ltmPercent",
      "overridePercent",
      "newProductOverride",
      "finalPercent",
      "eligibleFinalPercent",
      "subcategoryEligibleTotalPercent",
      "adjustedFinalPercent",
      "allocationTotalCheck",
      "forecastableCheck",
      "overrideConflictCheck",
      "forecastInputCheck",
      "totalUnitsCheck",
      "skuForecastRevenue",
      "subcategoryForecastRevenue",
      "annualUnits",
      "orderEligible",
      "leadTimeDays",
      "coverageMonthsTarget",
      "coverageDaysTarget",
      "targetInventoryUnits",
      "unitsSoldYtd",
      "lastClosedSalesMonth",
      "remainingAnnualDemand",
      "forecastUnitsNext3Months",
      "dailyDemand",
      "safetyStockDays",
      "leadTimeDemand",
      "safetyStockUnits",
      "reorderPointUnits",
      "daysUntilStockout",
      "stockoutDate",
      "nextPoRequiredDate",
      "replenishmentCashRequired",
      "onHandUnits",
      "onOrderUnits",
      "replacementOnHand",
      "replacementOnOrder",
      "projectedCoverageDays",
      "otbUnits",
      "annualOtbUnits",
      "totalBomCost",
      "estAnnualInventoryCost",
      "effectiveOnHand",
      "effectiveOnOrder",
      "unitsToOrder",
      "moq",
      "cartonQty",
      "containerQty",
      "containerGroup",
      "unitCost",
      "baseBuyUnits",
      "groupBaseBuy",
      "moqBuyUnits",
      "roundedUnits",
      "groupRemainder",
      "adjustedRoundedUnits",
      "roundingDelta",
      "roundingType",
      "containers",
      "containerUnits",
      "fillUnits",
      "fillAllocatedUnits",
      "cashRequired",
      "groupUtilization",
      "preFillUtilization",
      "procurementEligible",
      "needsReview",
      "exceptionSummary",
      "riskFlag",
      "approved",
      "approvedBy",
      "notes",
    ],
    "approved-buys": [
      "supplySku",
      "supplier",
      "productName",
      "category",
      "subCategory",
      "status",
      "orderEligible",
      "leadTimeDays",
      "coverageMonthsTarget",
      "coverageDaysTarget",
      "targetInventoryUnits",
      "annualUnits",
      "unitsSoldYtd",
      "lastClosedSalesMonth",
      "remainingAnnualDemand",
      "forecastUnitsNext3Months",
      "dailyDemand",
      "safetyStockDays",
      "leadTimeDemand",
      "safetyStockUnits",
      "reorderPointUnits",
      "daysUntilStockout",
      "stockoutDate",
      "nextPoRequiredDate",
      "replenishmentCashRequired",
      "onHandUnits",
      "replacementOnHand",
      "onOrderUnits",
      "replacementOnOrder",
      "projectedCoverageDays",
      "otbUnits",
      "annualOtbUnits",
      "totalBomCost",
      "estAnnualInventoryCost",
      "effectiveOnHand",
      "effectiveOnOrder",
      "unitsToOrder",
      "riskFlag",
      "approved",
      "approvedBy",
      "notes",
    ],
    "overstock-inventory": [
      "supplySku",
      "productName",
      "category",
      "subCategory",
      "status",
      "forecastable",
      "productLine",
      "launchDate",
      "inactiveDate",
      "supplier",
      "warehouse",
      "msrp",
      "annualUnits",
      "orderEligible",
      "leadTimeDays",
      "coverageMonthsTarget",
      "coverageDaysTarget",
      "targetInventoryUnits",
      "unitsSoldYtd",
      "lastClosedSalesMonth",
      "remainingAnnualDemand",
      "forecastUnitsNext3Months",
      "dailyDemand",
      "safetyStockDays",
      "leadTimeDemand",
      "safetyStockUnits",
      "reorderPointUnits",
      "daysUntilStockout",
      "stockoutDate",
      "nextPoRequiredDate",
      "replenishmentCashRequired",
      "onHandUnits",
      "onOrderUnits",
      "replacementOnHand",
      "replacementOnOrder",
      "projectedCoverageDays",
      "otbUnits",
      "annualOtbUnits",
      "totalBomCost",
      "estimatedAnnualInventoryCost",
      "effectiveOnHand",
      "effectiveOnOrder",
      "unitsToOrder",
      "riskFlag",
      "approved",	
      "approvedBy",
      "notes",
      "totalInventory",
      "targetCoverageDays",
      "overstockThreshold",
      "overstockFlag",
      "overstockLevel",
      "excessUnits",
      "excessValue",
      "action",
      "governance",
    ],
    "ai-rebalance": [],
    "demand-distribution-profile": [
      "supplySku",
      "productName",
      "rangeRevenue",
      "rangeShare",
      "ltmShare",
      "delta",
      "tier",
    ],
    "cash-liabilities": [
      "buyDate",
      "supplier",
      "productName",
      "supplySku",
      "unitsToOrder",
      "unitCost",
      "extendedCashLiability",
      "approvalStatus",
      "timingBucket",
      "notes",
    ],
    "upcoming-buys": [
      "buyDate",
      "supplier",
      "productName",
      "supplySku",
      "unitsToOrder",
      "cashToOrder",
      "approved",
      "notes",
    ],
    "po-schedule": [
      "scheduledPoDate",
      "supplier",
      "cadenceLabel",
      "scheduleType",
      "requiredByDate",
      "skuCount",
      "productCount",
      "unitsToOrder",
      "cashRequired",
      "approvedCount",
    ],
};

/** Default visible columns per view (aligned with client planning presets). */
export const DEFAULT_VISIBLE_COLUMN_IDS = {
    "sku-allocation": [
        "supplySku",
        "productName",
        "category",
        "subCategory",
        "status",
        "forecastable",
        "ltmRevenue",
        "ltmPercent",
        "overridePercent",
        "newProductOverride",
        "finalPercent",
        "eligibleFinalPercent",
        "subcategoryEligibleTotalPercent",
        "adjustedFinalPercent",
        "ytdTotals",
        "recalcTotal2025",
    ],
    "forecast-demand": [],
    "eligibility-validation": [
        "supplySku",
        "productName",
        "status",
        "forecastable",
        "overridePercent",
        "newProductOverride",
        "finalPercent",
        "eligibleFinalPercent",
        "subcategoryEligibleTotalPercent",
        "adjustedFinalPercent",
        "allocationTotalCheck",
        "forecastableCheck",
        "overrideConflictCheck",
    ],
    "monthly-trend": [
        "supplySku",
        "productName",
        "status",
        "ytdForecast",
        "ytdActual",
        "ytdVariance",
    ],
    historical: [
        "supplySku",
        "productName",
        "msrp",
        "category",
        "subCategory",
        "janRevenue",
        "febRevenue",
        "marRevenue",
        "aprRevenue",
        "mayRevenue",
        "junRevenue",
        "julRevenue",
        "augRevenue",
        "sepRevenue",
        "octRevenue",
        "novRevenue",
        "decRevenue",
        "ytdTotals",
    ],
    "forecast-vs-actuals": [
        "supplySku",
        "productName",
        "category",
        "subCategory",
        "ytdForecast",
        "ytdActual",
        "ytdVariance",
        "variancePercent",
        "varianceFlag",
        "overstockLevel",
        "excessUnits",
        "excessValue",
    ],
    "forecast-by-sku": [
        "supplySku",
        "msrp",
        "productName",
        "category",
        "subCategory",
        "status",
        "adjustedFinalPercent",
        "skuForecastRevenue",
        "subcategoryForecastRevenue",
        "annualUnits",
        "forecastInputCheck",
        "launchDate",
        "inactiveDate",
        "totalUnitsCheck",
        "janUnits",
        "febUnits",
        "marUnits",
        "aprUnits",
        "mayUnits",
        "junUnits",
        "julUnits",
        "augUnits",
        "sepUnits",
        "octUnits",
        "novUnits",
        "decUnits",
    ],
    "open-to-buy": [
        "supplySku",
        "supplier",
        "productName",
        "category",
        "subCategory",
        "status",
        "orderEligible",
        "leadTimeDays",
        "coverageMonthsTarget",
        "coverageDaysTarget",
        "targetInventoryUnits",
        "annualUnits",
        "unitsSoldYtd",
        "lastClosedSalesMonth",
        "remainingAnnualDemand",
        "forecastUnitsNext3Months",
        "dailyDemand",
        "safetyStockDays",
        "leadTimeDemand",
        "safetyStockUnits",
        "reorderPointUnits",
        "daysUntilStockout",
        "stockoutDate",
        "nextPoRequiredDate",
        "replenishmentCashRequired",
        "onHandUnits",
        "replacementOnHand",
        "onOrderUnits",
        "replacementOnOrder",
        "projectedCoverageDays",
        "otbUnits",
        "annualOtbUnits",
        "totalBomCost",
        "estAnnualInventoryCost",
        "effectiveOnHand",
        "effectiveOnOrder",
        "unitsToOrder",
        "moq",
        "cartonQty",
        "containerQty",
        "containerGroup",
        "unitCost",
        "baseBuyUnits",
        "groupBaseBuy",
        "moqBuyUnits",
        "roundedUnits",
        "groupRemainder",
        "adjustedRoundedUnits",
        "roundingDelta",
        "roundingType",
        "containers",
        "containerUnits",
        "fillUnits",
        "fillAllocatedUnits",
        "cashRequired",
        "groupUtilization",
        "preFillUtilization",
        "procurementEligible",
        "needsReview",
        "exceptionSummary",
        "riskFlag",
        "approved",
        "approvedBy",
        "notes",
    ],
    "approved-buys": [
        "supplySku",
        "supplier",
        "productName",
        "category",
        "subCategory",
        "status",
        "orderEligible",
        "leadTimeDays",
        "coverageMonthsTarget",
        "coverageDaysTarget",
        "targetInventoryUnits",
        "annualUnits",
        "unitsSoldYtd",
        "lastClosedSalesMonth",
        "remainingAnnualDemand",
        "forecastUnitsNext3Months",
        "dailyDemand",
        "safetyStockDays",
        "leadTimeDemand",
        "safetyStockUnits",
        "reorderPointUnits",
        "daysUntilStockout",
        "stockoutDate",
        "nextPoRequiredDate",
        "replenishmentCashRequired",
        "onHandUnits",
        "replacementOnHand",
        "onOrderUnits",
        "replacementOnOrder",
        "projectedCoverageDays",
        "otbUnits",
        "annualOtbUnits",
        "totalBomCost",
        "estAnnualInventoryCost",
        "effectiveOnHand",
        "effectiveOnOrder",
        "unitsToOrder",
        "riskFlag",
        "approved",
        "approvedBy",
        "notes",
    ],
    "overstock-inventory": [
        "supplySku",
        "productName",
        "category",
        "subCategory",
        "dailyDemand",
        "effectiveOnHand",
        "effectiveOnOrder",
        "totalInventory",
        "projectedCoverageDays",
        "targetCoverageDays",
        "overstockThreshold",
        "overstockFlag",
        "overstockLevel",
        "excessUnits",
        "excessValue",
        "action",
        "governance",
    ],
    "ai-rebalance": [
        "supplySku",
        "productName",
        "category",
        "subCategory",
        "adjustedFinalPercent",
        "variancePercent",
        "varianceFlag",
        "recommendedDelta",
        "offsetSku",
        "governanceReason",
        "recommendationStatus",
    ],
    "demand-distribution-profile": [
        "supplySku",
        "productName",
        "rangeRevenue",
        "rangeShare",
        "ltmShare",
        "delta",
        "tier",
    ],
    "cash-liabilities": [
        "buyDate",
        "supplier",
        "productName",
        "supplySku",
        "unitsToOrder",
        "unitCost",
        "extendedCashLiability",
        "approvalStatus",
        "notes",
    ],
    "upcoming-buys": [
        "buyDate",
        "supplier",
        "productName",
        "supplySku",
        "unitsToOrder",
        "cashToOrder",
        "approved",
        "notes",
    ],
    "po-schedule": [
        "scheduledPoDate",
        "supplier",
        "requiredByDate",
        "unitsToOrder",
        "cashRequired",
        "skuCount",
        "productCount",
        "approvedCount",
    ],
    "bundle-demand-explosion": [],
    "bundle-replacement-shift": [],
    "replacement-demand-shift": [],
    "purchase-rounding": [],
    "container-optimization": [],
    "inventory-capital-plan": [],
    "component-demand-ledger": [],
};

export const defaultViewTable = [
  'sku-allocation', 
  'eligibility-validation',
  'monthly-trend',
  'historical',
  'forecast-vs-actuals',
  'forecast-by-sku',
  'open-to-buy',
  'overstock-inventory',
];

/** Default-table views that render DefaultPlanningTable (subset of all planning views). */
export function isDefaultPlanningTableView(viewId) {
  return defaultViewTable.includes(viewId);
}

/**
 * Views whose preset includes SKU allocation derived % columns
 * (finalPercent, eligibleFinalPercent, subcategoryEligibleTotalPercent, adjustedFinalPercent).
 */
export function viewUsesSkuAllocationDerivedChecks(viewId) {
  if (!viewId) return false;
  const preset = PRESET_COLUMN_IDS[viewId];
  return Array.isArray(preset) && preset.includes("finalPercent");
}

export const LEFT_COLUMN_WIDTHS = { select: 44 };
export const manualInputBtn =
    "manual-input h-[25px] inline-flex items-center transition-colors rounded-[4px] bg-[#E8F0FE] border border-[#c5d8fc] hover:bg-[#d6e6fd] hover:border-[#a8c4fb]";
export const DEMAND_PLANNING_VIEW_PRESETS = [
    { id: "sku-allocation", name: "SKU Allocation", columnIds: PRESET_COLUMN_IDS["sku-allocation"], locked: true, 
        group: {
            id: "core-planning", label: "Core Planning", order: 2
        } 
    },
    { id: "forecast-demand", name: "Forecast Demand", columnIds: PRESET_COLUMN_IDS["forecast-demand"], locked: true, 
        group: {
            id: "core-planning", label: "Core Planning", order: 1
        }  
    },
    { id: "eligibility-validation", name: "Eligibility & Validation", columnIds: PRESET_COLUMN_IDS["eligibility-validation"], locked: true, 
        group: {
            id: "data-quality-controls", label: "Data Quality & Controls", order: 1
        }  
    },
    { id: "monthly-trend", name: "Monthly Trend", columnIds: PRESET_COLUMN_IDS["monthly-trend"], locked: true, 
        group: {
            id: "core-planning", label: "Core Planning", order: 6
        }  
    },
    { id: "historical", name: "Historical", columnIds: PRESET_COLUMN_IDS.historical, locked: true, 
        group: {
            id: "core-planning", label: "Core Planning", order: 7
        }  
    },
    { id: "forecast-vs-actuals", name: "Forecast Vs Actuals", columnIds: PRESET_COLUMN_IDS["forecast-vs-actuals"], locked: true, 
        group: {
            id: "core-planning", label: "Core Planning", order: 3
        }  
    },
    { id: "forecast-by-sku", name: "Forecast By SKU", columnIds: PRESET_COLUMN_IDS["forecast-by-sku"], locked: true, 
        group: {
            id: "core-planning", label: "Core Planning", order: 5
        }  
    },
    { id: "open-to-buy", name: "Open To Buy", columnIds: PRESET_COLUMN_IDS["open-to-buy"], locked: true, 
        group: {
            id: "buy-planning", label: "Buy Planning", order: 1
        }  
    },
    { id: "approved-buys", name: "Approved Buys", columnIds: PRESET_COLUMN_IDS["approved-buys"], locked: true, 
        group: {
            id: "buy-planning", label: "Buy Planning", order: 2
        }  
    },
    { id: "overstock-inventory", name: "Overstock Inventory", columnIds: PRESET_COLUMN_IDS["overstock-inventory"], locked: true, 
        group: {
            id: "capital-inventory-risk", label: "Capital & Inventory Risk", order: 3
        }  
    },
    { id: "ai-rebalance", name: "Demand Rebalance", columnIds: PRESET_COLUMN_IDS["ai-rebalance"], locked: true, 
        group: {
            id: "core-planning", label: "Core Planning", order: 4
        }  
    },
    { id: "demand-distribution-profile", name: "Demand Distribution Profile", columnIds: PRESET_COLUMN_IDS["demand-distribution-profile"], locked: true, 
        group: {
            id: "data-quality-controls", label: "Data Quality & Controls", order: 2
        }  
    },
    { id: "cash-liabilities", name: "Cash Liabilities", columnIds: PRESET_COLUMN_IDS["cash-liabilities"], locked: true, 
        group: {
            id: "capital-inventory-risk", label: "Capital & Inventory Risk", order: 2
        }  
    },
    { id: "upcoming-buys", name: "Upcoming Buys", columnIds: PRESET_COLUMN_IDS["upcoming-buys"], locked: true, 
        group: {
            id: "buy-planning", label: "Buy Planning", order: 3
        }  
    },
    // { id: "po-schedule", name: "PO Schedule", columnIds: PRESET_COLUMN_IDS["po-schedule"], locked: true, 
    //     group: {
    //         id: "buy-planning", label: "Buy Planning", order: 4
    //     }  
    // },
    { id: "purchase-rounding", name: "Purchase Rounding", columnIds: PRESET_COLUMN_IDS["purchase-rounding"], locked: true, 
        group: {
            id: "buy-planning", label: "Buy Planning", order: 5
        }  
    },
    { id: "bundle-demand-explosion", name: "Bundle Demand Explosion", columnIds: PRESET_COLUMN_IDS["bundle-demand-explosion"], locked: true, 
        group: {
            id: "demand-transformation-audit", label: "Demand Transformation Audit", order: 1
        }  
    },
    { id: "bundle-replacement-shift", name: "Bundle Replacement Shift", columnIds: PRESET_COLUMN_IDS["bundle-replacement-shift"], locked: true, 
        group: {
            id: "demand-transformation-audit", label: "Demand Transformation Audit", order: 3
        }  
    },
    { id: "replacement-demand-shift", name: "Replacement Demand Shift", columnIds: PRESET_COLUMN_IDS["replacement-demand-shift"], locked: true, 
        group: {
            id: "demand-transformation-audit", label: "Demand Transformation Audit", order: 2
        }  
    },
    { id: "component-demand-ledger", name: "Component Demand Ledger", columnIds: PRESET_COLUMN_IDS["component-demand-ledger"], locked: true, 
        group: {
            id: "demand-transformation-audit", label: "Demand Transformation Audit", order: 4
        }  
    },
    { id: "container-optimization", name: "Container Optimization", columnIds: PRESET_COLUMN_IDS["container-optimization"], locked: true, 
        group: {
            id: "container-optimization", label: "Container Optimization", order: 1
        }  
    },
    { id: "inventory-capital-plan", name: "Inventory Capital Plan", columnIds: PRESET_COLUMN_IDS["inventory-capital-plan"], locked: true, 
        group: {
            id: "capital-inventory-risk", label: "Capital & Inventory Risk", order: 1
        }  
    },
];

/** Always visible in demand planning tables (cannot be toggled off). */
export const DEMAND_PLANNING_PINNED_COLUMN_IDS = new Set(["supplySku"]);

/** Display labels for planning column ids; missing keys fall back to humanized ids. */
export const DEMAND_PLANNING_COLUMN_LABELS = {
    supplySku: "Supply SKU",
    productName: "Product Name",
    rangeRevenue: "Range revenue",
    rangeShare: "Range share",
    ltmShare: "LTM share",
    category: "Category",
    status: "Status",
    forecastable: "Forecastable",
    subCategory: "Subcategory",
    productLine: "Product Line",
    launchDate: "Launch Date",
    inactiveDate: "Inactive Date",
    supplier: "Supplier",
    ltmRevenue: "LTM Revenue",
    ltmPercent: "LTM %",
    overridePercent: "Override %",
    newProductOverride: "New Product Override",
    finalPercent: "Final %",
    eligibleFinalPercent: "Eligible Final %",
    subcategoryEligibleTotalPercent: "Subcat Eligible Total %",
    adjustedFinalPercent: "Adjusted Final %",
    allocationTotalCheck: "Allocation Total",
    forecastableCheck: "Forecastable",
    overrideConflictCheck: "Override Conflict",
    forecastInputCheck: "Forecast Input",
    totalUnitsCheck: "Total Units",
    skuForecastRevenue: "SKU Forecast Revenue",
    subcategoryForecastRevenue: "Subcategory Forecast Revenue",
    annualUnits: "Annual Units",
    janRevenue: `Jan ${new Date().getFullYear() - 1}`,
    febRevenue: `Feb ${new Date().getFullYear() - 1}`,
    marRevenue: `Mar ${new Date().getFullYear() - 1}`,
    aprRevenue: `Apr ${new Date().getFullYear() - 1}`,
    mayRevenue: `May ${new Date().getFullYear() - 1}`,
    junRevenue: `Jun ${new Date().getFullYear() - 1}`,
    julRevenue: `Jul ${new Date().getFullYear() - 1}`,
    augRevenue: `Aug ${new Date().getFullYear() - 1}`,
    sepRevenue: `Sep ${new Date().getFullYear() - 1}`,
    octRevenue: `Oct ${new Date().getFullYear() - 1}`,
    novRevenue: `Nov ${new Date().getFullYear() - 1}`,
    decRevenue: `Dec ${new Date().getFullYear() - 1}`,
    ytdTotals: "YTD Totals",
    recalcTotal2025: `Recalc Total ${new Date().getFullYear() - 1}`,
    subCategoryForecastRevenue: "Subcategory Forecast Revenue",
    msrp: "MSRP",
    planned_gross_price: "Planned Gross Price",
    warehouse: "Warehouse",
    totalInventory: "Total Inventory",
    targetCoverageDays: "Target Coverage (Days)",
    overstockThreshold: "Overstock Threshold",
    overstockFlag: "Overstock Flag",
    overstockLevel: "Overstock Level",
    excessUnits: "Excess Units",
    excessValue: "Excess Value",
    variancePercent: "Variance %",
    varianceFlag: "Variance Flag",
    unitsToOrder: "Units To Order",
    moq: "MOQ",
    cartonQty: "Carton Qty",
    containerQty: "Container Qty",
    containerGroup: "Container Group",
    moqBuyUnits: "MOQ Buy Units",
    baseBuyUnits: "Base Buy Units",
    groupBaseBuy: "Group Base Buy",
    roundedUnits: "Rounded Units",
    groupRemainder: "Group Remainder",
    adjustedRoundedUnits: "Adjusted Rounded Units",
    roundingDelta: "Rounding Delta",
    roundingType: "Rounding Type",
    containers: "Containers",
    containerUnits: "Container Units",
    fillUnits: "Fill Units",
    fillAllocatedUnits: "Fill Allocated Units",
    groupUtilization: "Group Utilization",
    preFillUtilization: "Pre-Fill Utilization",
    procurementEligible: "Procurement Eligible",
    needsReview: "Needs Review",
    exceptionSummary: "Exception Summary",
    cashToOrder: "Cash To Order",
    approvalCashLiability: "Approval Cash Liability",
    nextPoRequiredDate: "Next PO Date",
    totalBomCost: "Total BOM Cost",
    estimatedAnnualInventoryCost: "Est. Annual Inv. Cost",
    effectiveOnHand: "Effective On Hand",
    effectiveOnOrder: "Effective On Order",
    riskFlag: "Risk Flag",
    approvedBy: "Approved By",
    approved: "Approved",
    notes: "Notes",
    action: "Action",
    governance: "Governance",
    delta: "Delta",
    tier: "Tier",
    buyDate: "Buy date",
    unitCost: "Unit Cost",
    extendedCashLiability: "Extended cash liability",
    approvalStatus: "Approval status",
    timingBucket: "Timing bucket",
    scheduledPoDate: "Scheduled PO Date",
    cadenceLabel: "Cadence Label",
    scheduleType: "Schedule Type",
    requiredByDate: "Required By Date",
    skuCount: "SKU Count",
    productCount: "Product Count",
    unitsToOrder: "Units To Order",
    cashRequired: "Cash Required",
    approvedCount: "Approved Mix",
};

export function humanizeDemandPlanningColumnId(id) {
  const spaced = String(id)
    // mar2026 -> mar 2026
    .replace(/([a-zA-Z])(\d)/g, "$1 $2")
    // 2026Variance -> 2026 Variance
    .replace(/(\d)([a-zA-Z])/g, "$1 $2")
    // camelCase -> camel Case
    .replace(/([a-z])([A-Z])/g, "$1 $2");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function getDemandPlanningColumnLabel(id, { viewId, planningYear } = {}) {
    if (viewId === "sku-allocation" && Number.isFinite(planningYear)) {
        const priorYear = planningYear - 1;
        if (id === "ltmRevenue") {
            return `${priorYear} Actual Revenue`;
        }
        if (id === "ltmPercent") {
            return `${priorYear} Revenue %`;
        }
    }
    return DEMAND_PLANNING_COLUMN_LABELS[id] || humanizeDemandPlanningColumnId(id);
}

export const DEMAND_PLANNING_COLUMN_GROUP_LABELS = {
    identity: "Identity",
    allocation: "Allocation",
    historical: "Historical",
    trend: "Forecast & Trend",
    forecastBySku: "Forecast By SKU",
    inventory: "Open To Buy",
    overstock: "Overstock",
    aiRebalance: "AI Rebalance",
    planning: "Planning",
};

const DEMAND_PLANNING_IDENTITY_COLUMN_IDS = new Set([
    "supplySku",
    "productName",
    "category",
    "subCategory",
    "status",
    "forecastable",
    "productLine",
    "launchDate",
    "inactiveDate",
    "supplier",
    "warehouse",
    "msrp",
]);

const DEMAND_PLANNING_ALLOCATION_COLUMN_IDS = new Set([
    "ltmRevenue",
    "ltmPercent",
    "overridePercent",
    "newProductOverride",
    "finalPercent",
    "eligibleFinalPercent",
    "subcategoryEligibleTotalPercent",
    "adjustedFinalPercent",
    "allocationTotalCheck",
    "forecastableCheck",
    "overrideConflictCheck",
    "forecastInputCheck",
    "totalUnitsCheck",
    "skuForecastRevenue",
    "subcategoryForecastRevenue",
    "subCategoryForecastRevenue",
    "rangeRevenue",
    "rangeShare",
    "ltmShare",
    "delta",
    "tier",
]);

const DEMAND_PLANNING_HISTORICAL_COLUMN_IDS = new Set([
    "janRevenue",
    "febRevenue",
    "marRevenue",
    "aprRevenue",
    "mayRevenue",
    "junRevenue",
    "julRevenue",
    "augRevenue",
    "sepRevenue",
    "octRevenue",
    "novRevenue",
    "decRevenue",
    "ytdTotals",
    "recalcTotal2025",
]);

const DEMAND_PLANNING_FORECAST_BY_SKU_COLUMN_IDS = new Set([
    "janUnits",
    "febUnits",
    "marUnits",
    "aprUnits",
    "mayUnits",
    "junUnits",
    "julUnits",
    "augUnits",
    "sepUnits",
    "octUnits",
    "novUnits",
    "decUnits",
]);

const DEMAND_PLANNING_INVENTORY_COLUMN_IDS = new Set([
    "orderEligible",
    "leadTimeDays",
    "coverageMonthsTarget",
    "coverageDaysTarget",
    "targetInventoryUnits",
    "annualUnits",
    "unitsSoldYtd",
    "lastClosedSalesMonth",
    "remainingAnnualDemand",
    "forecastUnitsNext3Months",
    "dailyDemand",
    "safetyStockDays",
    "leadTimeDemand",
    "safetyStockUnits",
    "reorderPointUnits",
    "daysUntilStockout",
    "stockoutDate",
    "nextPoRequiredDate",
    "replenishmentCashRequired",
    "onHandUnits",
    "onOrderUnits",
    "replacementOnHand",
    "replacementOnOrder",
    "projectedCoverageDays",
    "otbUnits",
    "annualOtbUnits",
    "totalBomCost",
    "estAnnualInventoryCost",
    "estimatedAnnualInventoryCost",
    "effectiveOnHand",
    "effectiveOnOrder",
    "unitsToOrder",
    "moq",
    "cartonQty",
    "containerQty",
    "containerGroup",
    "unitCost",
    "baseBuyUnits",
    "groupBaseBuy",
    "moqBuyUnits",
    "roundedUnits",
    "groupRemainder",
    "adjustedRoundedUnits",
    "roundingDelta",
    "roundingType",
    "containers",
    "containerUnits",
    "fillUnits",
    "fillAllocatedUnits",
    "cashRequired",
    "groupUtilization",
    "preFillUtilization",
    "procurementEligible",
    "needsReview",
    "exceptionSummary",
    "cashToOrder",
    "approvalCashLiability",
    "riskFlag",
    "approved",
    "approvedBy",
    "notes",
]);

const DEMAND_PLANNING_OVERSTOCK_COLUMN_IDS = new Set([
    "totalInventory",
    "targetCoverageDays",
    "overstockThreshold",
    "overstockFlag",
    "overstockLevel",
    "excessUnits",
    "excessValue",
    "action",
    "governance",
]);

const DEMAND_PLANNING_AI_REBALANCE_COLUMN_IDS = new Set([
    "recommendedDelta",
    "offsetSku",
    "governanceReason",
    "recommendationStatus",
]);

const DEMAND_PLANNING_PLANNING_COLUMN_IDS = new Set([
    "buyDate",
    "unitCost",
    "extendedCashLiability",
    "approvalStatus",
    "timingBucket",
    "scheduledPoDate",
    "cadenceLabel",
    "scheduleType",
    "requiredByDate",
    "skuCount",
    "productCount",
    "cashRequired",
    "approvedCount",
]);

const DEMAND_PLANNING_TREND_SUFFIXES = ["Forecast", "Actual", "Variance"];

function isDemandPlanningTrendColumnId(id) {
    if (id === "variancePercent" || id === "varianceFlag") return true;
    if (id === "ytdForecast" || id === "ytdActual" || id === "ytdVariance") return true;
    if (/^ytd\d{4}(Forecast|Actual|Variance)$/.test(id)) return true;

    for (const month of MONTH_TREND_MONTH_PREFIXES) {
        for (const suffix of DEMAND_PLANNING_TREND_SUFFIXES) {
            if (id === `${month}${suffix}`) return true;
            if (new RegExp(`^${month}\\d{4}${suffix}$`).test(id)) return true;
        }
    }
    return false;
}

export function getDemandPlanningColumnGroup(id) {
    if (DEMAND_PLANNING_IDENTITY_COLUMN_IDS.has(id)) return "identity";
    if (DEMAND_PLANNING_ALLOCATION_COLUMN_IDS.has(id)) return "allocation";
    if (DEMAND_PLANNING_HISTORICAL_COLUMN_IDS.has(id)) return "historical";
    if (DEMAND_PLANNING_FORECAST_BY_SKU_COLUMN_IDS.has(id)) return "forecastBySku";
    if (DEMAND_PLANNING_INVENTORY_COLUMN_IDS.has(id)) return "inventory";
    if (DEMAND_PLANNING_OVERSTOCK_COLUMN_IDS.has(id)) return "overstock";
    if (DEMAND_PLANNING_AI_REBALANCE_COLUMN_IDS.has(id)) return "aiRebalance";
    if (DEMAND_PLANNING_PLANNING_COLUMN_IDS.has(id)) return "planning";
    if (isDemandPlanningTrendColumnId(id)) return "trend";
    return "identity";
}

export function groupDemandPlanningColumnSettings(columns) {
    const rows = Array.isArray(columns) ? columns : [];
    return Object.entries(DEMAND_PLANNING_COLUMN_GROUP_LABELS)
        .map(([groupId, label]) => ({
            groupId,
            label,
            columns: rows.filter((column) => getDemandPlanningColumnGroup(column.id) === groupId),
        }))
        .filter((group) => group.columns.length > 0);
}

/**
 * @param {string[]} presetColumnIds column ids for the preset (order preserved)
 * @param {{ visibility?: Record<string, boolean> } | undefined} viewMeta saved metadata entry for this view
 * @returns {string[]} preset columns that should render (respects saved visibility; pinned columns always included)
 */
/** Keeps first occurrence of each id (preset arrays must not repeat ids — repeats break React keys and headers). */
export function dedupeDemandPlanningColumnIds(ids) {
    if (!Array.isArray(ids)) return [];
    const seen = new Set();
    const out = [];
    for (const id of ids) {
        if (seen.has(id)) continue;
        seen.add(id);
        out.push(id);
    }
    return out;
}

export function hasDemandPlanningSavedColumnVisibility(viewMeta) {
    const vis = viewMeta?.visibility;
    return Boolean(vis && typeof vis === "object" && Object.keys(vis).length > 0);
}

export function getDemandPlanningDefaultVisibleColumnIds(viewId, presetColumnIds, planningYear = null) {
    const preset = new Set(dedupeDemandPlanningColumnIds(presetColumnIds));
    const template = appendClosedMonthTrendDefaults(
        viewId,
        DEFAULT_VISIBLE_COLUMN_IDS[viewId] || [],
        planningYear
    );
    return dedupeDemandPlanningColumnIds(template.filter((id) => preset.has(id)));
}

export function buildDemandPlanningColumnDrawerRows(
    presetColumnIds,
    viewId,
    viewMeta,
    planningYear = null
) {
    const rows = dedupeDemandPlanningColumnIds(presetColumnIds);
    const saved = hasDemandPlanningSavedColumnVisibility(viewMeta);
    const defaultIds = new Set(getDemandPlanningDefaultVisibleColumnIds(viewId, rows, planningYear));
    const visibility = viewMeta?.visibility || {};

    return rows.map((id) => ({
        id,
        label: getDemandPlanningColumnLabel(id, { viewId, planningYear }),
        pinned: DEMAND_PLANNING_PINNED_COLUMN_IDS.has(id),
        visible: DEMAND_PLANNING_PINNED_COLUMN_IDS.has(id)
            ? true
            : saved
              ? visibility[id] !== false
              : defaultIds.has(id),
    }));
}

export function applyDemandPlanningDefaultColumnVisibility(
    rows,
    viewId,
    presetColumnIds,
    planningYear = null
) {
    const defaultIds = new Set(getDemandPlanningDefaultVisibleColumnIds(viewId, presetColumnIds, planningYear));
    return rows.map((column) => ({
        ...column,
        visible: column.pinned ? true : defaultIds.has(column.id),
    }));
}

export function getVisibleDemandPlanningColumnIds(
    presetColumnIds,
    viewMeta,
    viewId,
    planningYear = null
) {
    const base = dedupeDemandPlanningColumnIds(presetColumnIds);
    let visible;
    if (!hasDemandPlanningSavedColumnVisibility(viewMeta)) {
        const defaultIds = new Set(getDemandPlanningDefaultVisibleColumnIds(viewId, base, planningYear));
        visible = base.filter((id) => DEMAND_PLANNING_PINNED_COLUMN_IDS.has(id) || defaultIds.has(id));
    } else {
        const vis = viewMeta.visibility;
        visible = base.filter((id) => {
            if (DEMAND_PLANNING_PINNED_COLUMN_IDS.has(id)) return true;
            return vis[id] !== false;
        });
    }

    if (TREND_VIEWS_WITH_CLOSED_MONTHS.has(viewId) && planningYear != null && planningYear !== "") {
        visible = filterTrendColumnsThroughClosedMonths(
            visible,
            countClosedPlanningYearMonths(planningYear)
        );
    }

    return visible;
}

export function isDemandPlanningColumnLayoutCustomized(
    viewId,
    presetColumnIds,
    viewMeta,
    planningYear = null
) {
    const rows = dedupeDemandPlanningColumnIds(presetColumnIds);
    if (!viewId || rows.length === 0) return false;

    const currentRows = buildDemandPlanningColumnDrawerRows(rows, viewId, viewMeta, planningYear);
    const defaultRows = applyDemandPlanningDefaultColumnVisibility(
        currentRows.map((column) => ({ ...column })),
        viewId,
        rows,
        planningYear
    );

    return currentRows.some(
        (column, index) => column.visible !== defaultRows[index]?.visible
    );
}

export const MAX_HISTORY = 1000;
export const INITIAL_SUB_CATEGORIES = [
  { id: "core-wallets", sub_category: "Core Wallets", category: "Wallets", pct_of_channel: 0.5133, actuals_2025: 15942449, ytd_actuals: 1786502, last_closed_date: "Mar 2026" },
  { id: "passport-wallets", sub_category: "Passport Wallets", category: "Wallets", pct_of_channel: 0.1823, actuals_2025: 3480102, ytd_actuals: 294066, last_closed_date: "Mar 2026" },
  { id: "luggage-set", sub_category: "Luggage Set", category: "Luggage", pct_of_channel: 0.07, actuals_2025: 228780, ytd_actuals: 260423, last_closed_date: "Mar 2026" },
  { id: "checked", sub_category: "Checked", category: "Luggage", pct_of_channel: 0.0438, actuals_2025: 0, ytd_actuals: 0, last_closed_date: "Mar 2026" },
  { id: "carry-on", sub_category: "Carry-On", category: "Luggage", pct_of_channel: 0.0612, actuals_2025: 69814, ytd_actuals: 75813, last_closed_date: "Mar 2026" },
  { id: "weekenders", sub_category: "Weekenders", category: "Travel Bags", pct_of_channel: 0.0256, actuals_2025: 195932, ytd_actuals: 27761, last_closed_date: "Mar 2026" },
  { id: "backpacks", sub_category: "Backpacks", category: "Travel Bags", pct_of_channel: 0.0155, actuals_2025: 262448, ytd_actuals: 36959, last_closed_date: "Mar 2026" },
  { id: "work-bags", sub_category: "Work Bags", category: "Travel Bags", pct_of_channel: 0.0028, actuals_2025: 20169, ytd_actuals: 9371, last_closed_date: "Mar 2026" },
  { id: "slings", sub_category: "Slings", category: "Travel Bags", pct_of_channel: 0.0002, actuals_2025: 73453, ytd_actuals: 3546, last_closed_date: "Mar 2026" },
  { id: "travel-org", sub_category: "Travel Organization", category: "Travel Bags", pct_of_channel: 0.0206, actuals_2025: 217971, ytd_actuals: 39810, last_closed_date: "Mar 2026" },
  { id: "edc-accessories", sub_category: "EDC Accessories", category: "Accessories", pct_of_channel: 0.0444, actuals_2025: 1074594, ytd_actuals: 141584, last_closed_date: "Mar 2026" },
  { id: "travel-accessories", sub_category: "Travel Accessories", category: "Accessories", pct_of_channel: 0.0103, actuals_2025: 189087, ytd_actuals: 14369, last_closed_date: "Mar 2026" },
  { id: "rings", sub_category: "Rings", category: "Rings", pct_of_channel: 0.01, actuals_2025: 47059, ytd_actuals: 16275, last_closed_date: "Mar 2026" },
];
export const INITIAL_MONTHS = [
  { month: "Jan", historical_pct: 0.0491, override_pct: 0.0602, actual_pct: 0.0591, actual_revenue: 1149146 },
  { month: "Feb", historical_pct: 0.0729, override_pct: 0.0693, actual_pct: 0.0486, actual_revenue: 946130 },
  { month: "Mar", historical_pct: 0.0542, override_pct: 0.0574, actual_pct: 0.0354, actual_revenue: 688999 },
  { month: "Apr", historical_pct: 0.0489, override_pct: 0.0488, actual_pct: null, actual_revenue: null },
  { month: "May", historical_pct: 0.0622, override_pct: 0.0587, actual_pct: null, actual_revenue: null },
  { month: "Jun", historical_pct: 0.0897, override_pct: 0.0832, actual_pct: null, actual_revenue: null },
  { month: "Jul", historical_pct: 0.058, override_pct: 0.0641, actual_pct: null, actual_revenue: null },
  { month: "Aug", historical_pct: 0.0577, override_pct: 0.06, actual_pct: null, actual_revenue: null },
  { month: "Sep", historical_pct: 0.0556, override_pct: 0.0418, actual_pct: null, actual_revenue: null },
  { month: "Oct", historical_pct: 0.0455, override_pct: 0.0427, actual_pct: null, actual_revenue: null },
  { month: "Nov", historical_pct: 0.141, override_pct: 0.1422, actual_pct: null, actual_revenue: null },
  { month: "Dec", historical_pct: 0.2651, override_pct: 0.2716, actual_pct: null, actual_revenue: null },
];
export const CATEGORY_LTM = {
  Wallets: 0.8902,
  "Travel Bags": 0.0321,
  Luggage: 0.0137,
  Accessories: 0.0542,
  Rings: 0.0022,
};
export const SUBCAT_LTM = {
  "core-wallets": 0.7306,
  "passport-wallets": 0.1596,
  "luggage-set": 0.0105,
  checked: 0.0,
  "carry-on": 0.0032,
  weekenders: 0.009,
  backpacks: 0.012,
  "work-bags": 0.0009,
  slings: 0.0034,
  "travel-org": 0.01,
  "edc-accessories": 0.0493,
  "travel-accessories": 0.0087,
  rings: 0.0022,
};
export const parseIsoDate = (value) => {
  if (!value) return null;
  const parsed = new Date(`${value}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};
export const formatDemandPlanningDate = (value) => {
  if (!value) return "—";
  const parsed = parseIsoDate(value);
  return parsed
    ? parsed.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      })
    : "—";
};
export const fmt$ = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
export const fmtCompact$ = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n);
export const fmtPct = (n) => (n * 100).toFixed(2) + "%";
export const fmtDelta = (n) => {
  const s = fmt$(Math.abs(n));
  return n >= 0 ? `+${s}` : `-${s}`;
};
export const deltaClass = (n) => (n >= 0 ? "text-emerald-700 font-medium" : "text-red-600 font-medium");
export const finalMonthPct = (row) => row.override_pct ?? row.historical_pct ?? 0;
export const monthlyTarget = (revTarget, row) => Math.round(revTarget * finalMonthPct(row));
export const channelTarget = (revTarget, pct) => Math.round(revTarget * pct);
export const categoryTarget = (revTarget, pct) => Math.round(revTarget * pct);
export const subCatTarget = (revTarget, pct) => Math.round(revTarget * pct);
export const defaultWidthForColumn = (id) => {
  if (id === "supplySku") return 150;
  if (id === "productName") return 200;
  if (id === "overridePercent") return 150;
  if (id === "newProductOverride") return 150;
  if (id.endsWith("Check")) return 150;
  if (id === "varianceFlag") return 140;
  if (
      id === "groupUtilization" ||
      id === "preFillUtilization" ||
      id.includes("Percent") ||
      id === "variancePercent" ||
      id.endsWith("Pct")
  )
      return 88;
  if (
      id.endsWith("Forecast") ||
      id.endsWith("Actual") ||
      (id.endsWith("Variance") && id !== "variancePercent")
  )
      return 112;
  if (
      id.includes("Revenue") ||
      id.includes("Totals") ||
      id.includes("Inventory") ||
      id.includes("Cost") ||
      id.includes("Cash") ||
      id === "msrp" ||
      id === "excessValue"
  )
      return 112;
  if (id.includes("Date")) return 104;
  if (id === "approved") return 145;
  if (id === "notes" || id === "action" || id === "governance") return 140;
  return 104;
};
export const formatPlanningCell = (columnId, value) => {
  if (value === null || value === undefined || value === "") {
      return "—";
  }
  if (typeof value === "boolean") {
      return value ? "Yes" : "No";
  }
  if (typeof value === "number") {
      if (
        columnId === "groupUtilization" ||
        columnId === "preFillUtilization"
      ) {
        return fmtPct(value);
      }
      if (
        columnId === "cashRequired" ||
        columnId === "cashToOrder" ||
        columnId === "extendedCashLiability" ||
        columnId === "approvalCashLiability"
      ) {
        if (Math.abs(value) >= 1000000) {
          return fmtCompact$(value);
        }
        return fmt$(value);
      }
      if (
        columnId.includes("replenishmentCashRequired") ||
        columnId.includes("totalBomCost")
      ) {
        return fmtCompact$(value);
      }
      if (columnId.includes("totalInventory")) {
        return String(value);
      }
      if (
          columnId.includes("Percent") ||
          columnId === "variancePercent" ||
          columnId.endsWith("Pct")
      ) {
          return fmtPct(value);
      }
      const monthlyTrendUsd =
          columnId !== "variancePercent" &&
          (columnId.endsWith("Forecast") ||
              columnId.endsWith("Actual") ||
              (columnId.endsWith("Variance") && columnId !== "variancePercent"));
      if (monthlyTrendUsd) { 
          if (Math.abs(value) >= 1000000) {
              return fmtCompact$(value);
          }
          return fmt$(value);
      }
      if (
          columnId.includes("Revenue") ||
          columnId.includes("Totals") ||
          columnId === "msrp" ||
          columnId === "planned_gross_price" ||
          columnId.includes("Cash") ||
          columnId === "excessValue" ||
          columnId === "estAnnualInventoryCost" ||
          columnId === "estimatedAnnualInventoryCost" ||
          columnId.includes("Cost") ||
          columnId.includes("recalcTotal2025")
      ) {
          if (Math.abs(value) >= 1000000) {
              return fmtCompact$(value);
          }
          return fmt$(value);
      }
      if (Number.isFinite(value) && Math.abs(value - Math.round(value)) < 1e-9) {
          return String(Math.round(value));
      }
      return String(value);
  }
  return String(value);
};
export const safeString = (value, fallback = "") => {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : fallback;
};